import { useEffect } from 'react';

interface SEOSchemaProps {
  type: 'organization' | 'localBusiness' | 'product' | 'faq';
  data?: Record<string, unknown>;
}

export default function SEOSchema({ type, data }: SEOSchemaProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    let schema: Record<string, unknown> = {};

    if (type === 'organization') {
      schema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Nationwide Pallet Solution',
        url: siteUrl,
        logo: `${siteUrl}/vite.svg`,
        description: 'Leading supplier of 48x40 pallets, custom pallets, standard pallets, and GMA certified pallets nationwide with fast delivery and competitive pricing.',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Shreveport',
          addressRegion: 'LA',
          postalCode: '71108',
          addressCountry: 'US'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+1-318-315-2367',
          contactType: 'Sales',
          email: 'Lamar@NationwidePalletSolution.com',
          availableLanguage: 'English'
        },
        sameAs: []
      };
    } else if (type === 'localBusiness') {
      schema = {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#localbusiness`,
        name: 'Nationwide Pallet Solution',
        image: `${siteUrl}/vite.svg`,
        url: siteUrl,
        telephone: '+1-318-315-2367',
        email: 'Lamar@NationwidePalletSolution.com',
        priceRange: '$$',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '',
          addressLocality: 'Shreveport',
          addressRegion: 'LA',
          postalCode: '71108',
          addressCountry: 'US'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 32.5252,
          longitude: -93.7502
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '18:00'
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '09:00',
            closes: '14:00'
          }
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          reviewCount: '150'
        }
      };
    } else if (type === 'faq') {
      schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What types of pallets do you offer?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We offer Standard 48x40 4 Way Pallets #1, Standard 48x40 4 Way Pallets #2, 48x40 2 Way Pallets, and Custom Pallets in various grades to meet all your shipping and storage needs.'
            }
          },
          {
            '@type': 'Question',
            name: 'Do you deliver pallets nationwide?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, we provide nationwide delivery for all our pallet products including 48x40 pallets, custom pallets, standard pallets, and GMA certified pallets across the United States.'
            }
          },
          {
            '@type': 'Question',
            name: 'How quickly can I get a quote?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We provide quotes within 24 hours of receiving your request. Simply fill out our quotation form with your requirements, and our team will respond promptly with competitive pricing.'
            }
          },
          {
            '@type': 'Question',
            name: 'Do you offer pallet removal services?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, we offer free pallet removal services for businesses. We can pick up your unwanted pallets and either recycle them or repurpose them, helping you maintain a clean and organized workspace.'
            }
          }
        ]
      };
    } else if (type === 'product' && data) {
      schema = data;
    }

    const scriptId = `schema-${type}`;
    let scriptElement = document.getElementById(scriptId);

    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = scriptId;
      scriptElement.type = 'application/ld+json';
      document.head.appendChild(scriptElement);
    }

    scriptElement.textContent = JSON.stringify(schema);

    return () => {
      const element = document.getElementById(scriptId);
      if (element) {
        element.remove();
      }
    };
  }, [type, data]);

  return null;
}
